import { X } from "@/components/ui/icons";
import { cva, type VariantProps } from "class-variance-authority";
import {
  createContext,
  type ReactNode,
  type Ref,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ScrollViewInstance } from "react-native";
import {
  FlatList,
  type FlatListProps,
  Keyboard,
  Modal,
  Platform,
  Pressable,
  type ScrollViewProps,
  type StyleProp,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { useKeyboardState } from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type { ListProps } from "@/components/ui/FlashList";
import { SheetScrollView } from "@/components/ui/SheetScrollView";
import { SwmSheet } from "@/components/ui/SwmSheet";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useIsDesktop } from "@/hooks/useResponsive";
import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

/**
 * How the enclosing `Sheet` meets the keyboard, for its scroller.
 *
 * A sheet with `keyboardAvoiding` lifts itself above the keyboard, so its scroller must not add the
 * keyboard's height again as bottom space: doing so scrolled the split-payment rows away behind
 * blank space (TRO-1531). It must still bring a focused field into view, because a lifted sheet is
 * capped below the status bar and its body scrolls (TRO-1533); the field then has to clear the
 * pinned footer, which sits between the scroller and the keyboard, hence `footerHeight`. Sheets that
 * do not lift keep the plain keyboard-aware scroller (TRO-1435).
 */
const SheetKeyboard = createContext<{ lifted: boolean; footerHeight: number; }>({
  lifted: false,
  footerHeight: 0,
});

const backdrop = cva("flex-1", {
  variants: {
    // How dark the ground behind the sheet goes. `strong` is the picker family (sort, game,
    // card type) and the destructive reset confirm, which sit over busier surfaces.
    scrim: { default: "bg-black/40", strong: "bg-black/50" },
    // A bottom sheet suits a phone, whose reachable area is the bottom of the screen. The desktop
    // shell has no such edge, so the same content presents as a centered dialog, as desktop
    // conventions expect.
    //
    // This is the presentation for one answer. Content that is a place the operator manages gets
    // its own desktop route instead and keeps this sheet below the breakpoint; ADR-0135 is the
    // rule for which is which.
    placement: { sheet: "justify-end", dialog: "items-center justify-center p-6" },
  },
  defaultVariants: { scrim: "default", placement: "sheet" },
});

type Props = VariantProps<typeof backdrop> & {
  /**
   * Omit to render whenever mounted, for pickers their parent mounts conditionally rather than
   * toggles (`RN Modal` itself defaults `visible` to true).
   */
  visible?: boolean;
  /** Backdrop tap, hardware back, and web Escape all route here. */
  onClose: () => void;
  /**
   * Runs once the closing slide has finished, on iOS only (React Native's `Modal` reports it
   * nowhere else). For a flow that opens a second sheet in this one's place: iOS cannot present a
   * Modal while another is still dismissing, so the next one waits for this.
   */
  onDismiss?: () => void;
  children: ReactNode;
  /**
   * The dialog's accessible name. Supplying it also announces the panel as a web dialog
   * (`role="dialog"`, `aria-modal` and the name), which react-native-web honors; it drops the
   * native-only `accessibilityViewIsModal` the panel always carries. Omit it for a sheet whose own
   * heading already names it.
   */
  label?: string;
  /**
   * Names the backdrop as a dismiss control, for sheets whose only other exit is a deliberate
   * confirm. Otherwise the backdrop stays an unannounced scrim.
   */
  backdropLabel?: string;
  /**
   * Lifts the panel above the keyboard through Keyboard Controller on both native platforms.
   * Sheets with a text field want this; a list-only picker does not, because the wrapper would
   * constrain its height.
   */
  keyboardAvoiding?: boolean;
  /**
   * A tap on the panel body outside a field dismisses the keyboard without closing the sheet.
   * Pairs with `keyboardAvoiding` on the form sheets.
   */
  dismissKeyboardOnPress?: boolean;
  /** The native pull-down handle. Off for pickers that lead with their own close control. */
  grabber?: boolean;
  /**
   * Merged over the grab handle's own classes. Spacing only, for a sheet with a tighter gap under
   * its pill than the rest.
   */
  grabberClassName?: string;
  /**
   * `slide` (default) slides up, falling back to a crossfade under Reduce Motion. `fade` always
   * crossfades, for a sheet that must never slide regardless of the motion setting.
   */
  animation?: "slide" | "fade";
  /**
   * The share of the screen below the status bar the sheet may take, for a sheet that can outgrow
   * it. A content-sized sheet omits it and grows to what it holds, which most sheets want. Otherwise
   * pick the ceiling for what the sheet is: `0.9` for a full task (a form, a review list) that should
   * still show a strip of the screen behind it, or nearer `0.7` for a picker the user is choosing from
   * the screen behind, as with Inventory's Filters, where a taller ceiling hides the search and sort
   * rows being narrowed. Do not add one-off values.
   *
   * It is a prop rather than a `max-h-[90%]` class because the native sheet sizes itself from the
   * panel, so a percentage on the panel would resolve against the panel itself.
   *
   * A ceiling requires the content to shrink. React Native defaults `flexShrink: 0`, so a
   * `ScrollView` under a ceiling sizes itself to its full content, overflows the panel instead of
   * scrolling inside it, and renders its tail outside the sheet where nothing can reach it, with a
   * pinned footer painting over the row at the cut. A scroller that is a sheet's own child
   * therefore needs `className="shrink"`; see `inventory/FilterSheet` for an example.
   */
  ceiling?: number;
  /** Take the whole `ceiling` rather than sizing to the content, for a body that is one `flex-1` list. */
  fill?: boolean;
  /** Merged over the panel's own classes (extra padding, gaps). Height ceilings go in `ceiling`. */
  className?: string;
  /** Native panel sizing that cannot resolve from utility percentages inside the sheet wrapper. */
  style?: StyleProp<ViewStyle>;
  testID?: string;
  /**
   * Render into an ancestor's native Modal instead of opening another one. Only the web `Modal`
   * sheet reads it: the native sheet opens in its own window above any Modal.
   */
  hosted?: boolean;
  /**
   * The sheet's heading, rendered as the shared title row.
   *
   * Passing the string gives every sheet the same 17px `font-title` line and the
   * `accessibilityRole="header"` a screen reader needs to jump between headings, instead of each
   * sheet writing its own. Hand-written headings drift in classes, such as a `font-bold` RN cannot
   * synthesize from a static face (DESIGN §6), and often omit the role.
   */
  title?: string;
  /**
   * The line under the title: what this sheet is for, in a sentence. Rendered as `font-body text-sm
   * text-muted-foreground`, so it travels with the title rather than being re-typed after it.
   */
  subtitle?: string;
  /**
   * An explicit dismiss control beside the title, for a sheet whose backdrop tap and pull-down are
   * not enough on their own, such as a long form or one reached from a control the user can no
   * longer see. Off by default, because the grabber and the scrim already signal that it closes.
   */
  closeButton?: boolean;
  /**
   * A pinned action row below the body: the sheet's Done, Save or Confirm.
   *
   * It is a prop rather than a last child because of the flex layout. As a sibling in the caller's
   * JSX it competes with the body for height, and React Native's `flexShrink: 0` default lets the
   * body win: the scroller sizes to its full content, overflows a ceilinged panel instead of
   * scrolling inside it, and the footer paints over the row at the cut. Owning both halves here lets
   * the footer take the height it needs first and the body shrink into what is left.
   */
  footer?: ReactNode;
};

/**
 * The shared bottom sheet: the rounded panel with its safe-area inset, title, footer and scrim
 * every modal sheet needs.
 *
 * On iOS and Android it is Software Mansion's native bottom sheet (`SwmSheet`), which owns the
 * drag, the scrim tap, Android Back and the hand-off with a scroller in the body. On the web builds
 * (the desktop shell and the public browse storefront) it renders through React Native's own
 * `Modal`, which provides the focus trap, `aria-modal`, `role="dialog"`, Escape-to-close and modal
 * stacking (ADR-0040); the native sheet has no web build. A web flow that already owns a Modal can
 * host the same content (`hosted`) instead of stacking a second one.
 *
 * Motion honors the OS Reduce Motion setting per sheet: the native sheet appears without its
 * slide, because a full slide can be nauseating for motion-sensitive users.
 */
export function Sheet({
  visible,
  onClose,
  onDismiss,
  children,
  label,
  backdropLabel,
  keyboardAvoiding = false,
  dismissKeyboardOnPress = false,
  grabber = true,
  grabberClassName,
  animation = "slide",
  scrim,
  ceiling,
  fill = false,
  className,
  style,
  testID,
  hosted = false,
  title,
  subtitle,
  closeButton = false,
  footer,
}: Props) {
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const { foreground: closeGlyph } = useThemeColors();
  const reduced = useReducedMotion();
  // The same threshold the desktop shell uses (ADR-0020), so chrome and dialogs switch together
  // and the app is never half-adapted at any width.
  const dialog = useIsDesktop();
  // The phone sheet on iOS and Android is the native one; only web renders the Modal below, as a
  // dialog at desktop widths.
  const native = Platform.OS !== "web";
  const lifted = keyboardAvoiding && native;
  const keyboardHeight = useSheetKeyboardHeight();
  const keyboardUp = keyboardHeight > 0;
  const [footerHeight, setFooterHeight] = useState(0);
  const sheetKeyboard = useMemo(() => ({ lifted, footerHeight }), [lifted, footerHeight]);

  const panelBody = (
    <Pressable
      testID={testID}
      accessible={false}
      // Native isolation is unconditional. react-native-web drops this prop, so a named sheet
      // also gets the ARIA pair the web honors (see `label`). The two never conflict.
      accessibilityViewIsModal
      {...(label
        ? { role: "dialog" as const, "aria-modal": true, accessibilityLabel: label }
        : null)}
      className={cn(
        dialog
          ? "w-full max-w-[560px] rounded-2xl border border-border bg-bg px-6 pt-6"
          : "rounded-t-2xl border-t border-border bg-bg px-4 pt-4",
        className,
      )}
      // A centered dialog clears the home indicator by construction; only the docked sheet
      // needs the bottom inset, and it needs all of it. Under the iOS 26 floating tab bar,
      // `insets.bottom` covers the bar as well as the home indicator, because that bar is
      // UIKit's own: it draws above a transparent RN Modal and takes the taps in that strip, so
      // any part of the inset the sheet does not pad is unreadable and unreachable. Where
      // nothing floats over the sheet the inset is the indicator's alone, so this needs no
      // branch.
      //
      // While a lifted sheet rides the keyboard, the keyboard covers that strip, so the inset
      // would only push the footer up by the indicator's height for nothing.
      //
      // The docked panel may shrink to the room it has, rather than overflow the wrapper's top:
      // its body (`SheetScroll`, already `shrink`) then scrolls instead of the header leaving the
      // screen.
      style={StyleSheet.flatten([
        {
          // The native sheet does not avoid the keyboard, so a lifted one grows by its height.
          paddingBottom: dialog
            ? 24
            : lifted && keyboardUp
            ? 16 + (native ? keyboardHeight : 0)
            : insets.bottom + 16,
          ...(dialog ? null : { flexShrink: 1, minHeight: 0 }),
          // The native sheet caps its own root instead (see `SwmSheet`).
          // The native sheet sizes itself from the panel, so its ceiling is in points; a
          // percentage would resolve against the panel itself.
          ...(native
            ? { [fill ? "height" : "maxHeight"]: (windowHeight - insets.top - 8) * (ceiling ?? 1) }
            : ceiling
            ? { [fill ? "height" : "maxHeight"]: `${ceiling * 100}%` }
            : null),
        },
        style,
      ])}
      // Always swallow the press so a tap inside the sheet cannot reach the dismissing backdrop.
      onPress={(e) => {
        e.stopPropagation();
        // Web has no software keyboard to dismiss, and `Keyboard.dismiss()` there resolves to
        // `blurTextInput(currentlyFocusedField())`, which would blur the field being clicked and
        // make the sheet impossible to type into.
        if (dismissKeyboardOnPress && Platform.OS !== "web") Keyboard.dismiss();
      }}
    >
      {grabber && !dialog
        ? (
          <View
            className={cn(
              "-mb-3 -mt-4 h-12 items-center justify-center",
              grabberClassName,
            )}
            accessible={false}
          >
            <View className="h-1 w-10 rounded-full bg-border" />
          </View>
        )
        : null}
      {title || closeButton
        ? (
          <View className="mb-4 flex-row items-start gap-3">
            {title
              ? (
                <View className="min-w-0 flex-1 gap-1">
                  <SheetTitle>{title}</SheetTitle>
                  {subtitle
                    ? (
                      <Text className="font-body text-sm text-muted-foreground">
                        {subtitle}
                      </Text>
                    )
                    : null}
                </View>
              )
              : <View className="flex-1" />}
            {closeButton
              ? (
                <Pressable
                  onPress={onClose}
                  hitSlop={4}
                  accessibilityRole="button"
                  accessibilityLabel={`Close ${title ?? "sheet"}`}
                  className="h-12 w-12 items-center justify-center rounded-lg active:bg-surface-muted"
                >
                  <X size={18} color={closeGlyph} />
                </Pressable>
              )
              : null}
          </View>
        )
        : null}
      {
        /* `shrink` on the body only when there is a footer to yield to. With one, the two share
          a ceilinged column and the body must give; without one, the body is the whole column
          and shrinking it could collapse a short sheet below its content. */
      }
      {footer ? <View className="min-h-0 shrink">{children}</View> : children}
      {footer
        ? (
          <View
            className="mt-4"
            // Measured with its top margin so a lifted body knows how much footer to clear.
            onLayout={(event) => setFooterHeight(event.nativeEvent.layout.height + 16)}
          >
            {footer}
          </View>
        )
        : null}
    </Pressable>
  );

  if (native) {
    return (
      <SwmSheet
        open={visible !== false}
        onClose={onClose}
        onDismiss={onDismiss}
        animateIn={animation === "slide" && !reduced}
        scrimColor={scrim === "strong" ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.4)"}
      >
        <SheetKeyboard.Provider value={sheetKeyboard}>{panelBody}</SheetKeyboard.Provider>
      </SwmSheet>
    );
  }

  // The rest is the web Modal. `justifyContent: "flex-end"` is what makes a bottom sheet touch the
  // bottom edge.
  //
  // A caller's `ceiling` lands on the panel as a percentage max-height. Yoga resolves a percentage
  // max against the parent and then sizes the parent to that maximum rather than to the child it
  // laid out, so the wrapper can be taller than the panel. Without pinning, the panel sits at the
  // top of the wrapper with transparent space below it. Pinning it to the wrapper's end works
  // whatever ceiling a caller passes.
  //
  // `box-none` because the over-reserved strip is invisible but not inert: it would otherwise
  // swallow taps meant for the dismissing backdrop behind it.
  //
  // `flex: 1` rather than `maxHeight: "100%"` so the reservation also happens on the web, where CSS
  // resolves the panel's percentage max against this wrapper. A content-sized wrapper would give the
  // panel a ceiling that is a fraction of what the body wants, and a shrinking body would collapse
  // to almost nothing.
  const panel = (
    <View
      pointerEvents="box-none"
      style={{
        flex: 1,
        // A centered dialog must stay centered: pinning it to the end of an over-reserved wrapper
        // would drop it below the middle of the window by the slack described above.
        justifyContent: dialog ? "center" : "flex-end",
        // A docked sheet stops below the status bar. Lifted over the keyboard, a tall body used to
        // push the panel up past the top inset, so the title and close button sat under the clock
        // and the first rows could not be reached (TRO-1533).
        paddingTop: dialog ? 0 : insets.top + 8,
      }}
    >
      {panelBody}
    </View>
  );

  const content = (
    <View className={backdrop({ scrim, placement: dialog ? "dialog" : "sheet" })}>
      <Pressable
        accessible={!!backdropLabel}
        className="absolute inset-0"
        onPress={onClose}
        {...(backdropLabel
          ? { accessibilityRole: "button" as const, accessibilityLabel: backdropLabel }
          : null)}
      />
      {panel}
    </View>
  );
  const gestureContent = (
    // Hosted, it fills the host rather than flexing: two open hosted sheets (the scan queue and the
    // graded sheet over it) would otherwise split the Modal in half instead of stacking.
    <GestureHandlerRootView style={hosted ? StyleSheet.absoluteFill : { flex: 1 }}>
      {content}
    </GestureHandlerRootView>
  );
  if (hosted) return visible === false ? null : gestureContent;
  return (
    <Modal
      visible={visible}
      transparent
      // A dialog that slides up from the bottom edge looks like a sheet, so it crossfades in place
      // instead (TRO-560).
      //
      // Nothing slides on web at any width (TRO-550). `react-native-web` implements
      // `animationType="slide"` by translating the whole modal container, scrim included, so the
      // dimmed background would rise with the panel instead of staying still behind it. That also
      // applies to a phone browser, where `dialog` is false, so `Platform.OS === "web"` covers the
      // dialog case too.
      animationType="fade"
      onRequestClose={onClose}
      onDismiss={onDismiss}
    >
      {gestureContent}
    </Modal>
  );
}

/**
 * The scroller a sheet's own body uses, with `flexShrink: 1`.
 *
 * React Native defaults `flexShrink: 0`. Under a height ceiling the scroller then lays itself out at
 * its full content height and never scrolls: it overflows the panel and renders its tail outside
 * the sheet, where a pinned footer paints over the row at the cut and nothing below can be reached.
 * It looks like a clipping bug but is a sizing one.
 *
 * On native, focus tracking keeps an edited field above the keyboard when the sheet shrinks.
 * Naming it makes the contract greppable: a `ScrollView` directly inside a `Sheet` is a bug, and
 * this is the replacement (`SheetList` for a virtualized body). `Sheet` shrinks the body wrapper
 * around it, and this shrinks the scroller inside that wrapper. Both are needed, because a
 * shrinking parent cannot compress a child that does not shrink.
 *
 * The content sits in a view that claims, on native, any touch starting on static content (text, an
 * image, a gap). Unclaimed, that touch bubbles up to the panel's `Pressable`, and while the panel
 * holds it the body does not scroll: a sheet that is mostly a label preview could not be scrolled
 * at all (TRO-1580). A drag that started on a button or checkbox always scrolled, because that
 * control claimed it first. Released without scrolling, the touch dismisses the keyboard, as a
 * scroller's own tap would, unless the caller keeps it with `keyboardShouldPersistTaps="always"`. The
 * content-container props style that view, so callers' gaps and padding are unchanged.
 */
export function SheetScroll(
  {
    className,
    contentContainerClassName,
    contentContainerStyle,
    children,
    ...rest
  }:
    & ScrollViewProps
    & {
      className?: string;
      /**
       * Declared because React 19 passes `ref` as an ordinary prop, so `rest` already carries it to
       * the `ScrollView`; without this the type refuses what the runtime does. A body that scrolls
       * itself to a section needs it (`ShowDetailsEditor`).
       */
      ref?: Ref<ScrollViewInstance>;
    },
) {
  const { lifted, footerHeight } = useContext(SheetKeyboard);
  const keyboardHeight = useKeyboardState((state) => state.height);
  return (
    <SheetScrollView
      {...rest}
      {
        // Lifted: no added keyboard space, and a focused field scrolls up past the footer and the
        // panel's bottom padding rather than to the keyboard's edge. Otherwise the defaults.
        ...(lifted
          ? { extraKeyboardSpace: -keyboardHeight, bottomOffset: footerHeight + 16 + 12 }
          : null)
      }
      className={cn("shrink", className)}
    >
      <View
        className={contentContainerClassName}
        style={contentContainerStyle}
        onStartShouldSetResponder={Platform.OS === "web" ? undefined : () => true}
        onResponderRelease={Platform.OS === "web" || rest.keyboardShouldPersistTaps === "always"
          ? undefined
          : () => Keyboard.dismiss()}
      >
        {children}
      </View>
    </SheetScrollView>
  );
}

/**
 * `SheetScroll`'s virtualized twin, for sheet bodies that are one long list, such as the StockGroup
 * picker, a pull sheet's lines and Browse's set facets.
 *
 * It does the same job: `flexShrink: 1` so the list scrolls inside a ceilinged panel instead of
 * overflowing it. A bare `FlashList` inside a `Sheet` has the same sizing bug as a bare
 * `ScrollView`.
 *
 * It uses `FlatList`, not the recycler, on every platform, because a sheet body must measure
 * itself. A ceilinged sheet is content-sized: the panel grows to what its body reports and stops at
 * the ceiling. `FlatList` is a `ScrollView`, so it reports its content height. Shopify's list fills
 * whatever box it is given (`flex: 1`, flex-basis 0), which contributes nothing to an auto-height
 * parent, so on iOS and Android the body measures 0 and no rows render. `FlatList` still
 * virtualizes, and these sheets hold tens of rows, not thousands.
 */
export function SheetList<ItemT>(
  { className, ...rest }: ListProps<ItemT>,
) {
  return (
    <FlatList
      {
        // The two list types are nominally unrelated; `ListProps` is the slice both honor, and this
        // cast bridges them, as `FlashList.web.tsx` does.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above; the bridge is this line alone.
        ...(rest as unknown as FlatListProps<ItemT>)
      }
      className={cn("shrink", className)}
    />
  );
}

/**
 * The keyboard's height as the native sheet sees it, 0 while it is down.
 *
 * On Android the sheet lives in its own dialog window, which Keyboard Controller does not observe,
 * so it reports no keyboard there; React Native's own `Keyboard` events do, though only once the
 * keyboard has finished animating, and without the system bar below it, which is added back
 * (software-mansion-labs/react-native-bottom-sheet#66). iOS keeps Keyboard Controller, which tracks
 * the keyboard as it moves.
 */
function useSheetKeyboardHeight(): number {
  const controllerHeight = useKeyboardState((state) => (state.isVisible ? state.height : 0));
  const { bottom } = useSafeAreaInsets();
  const [androidHeight, setAndroidHeight] = useState(0);
  useEffect(() => {
    if (Platform.OS !== "android") return undefined;
    const shown = Keyboard.addListener(
      "keyboardDidShow",
      (event) => setAndroidHeight(event.endCoordinates.height),
    );
    const hidden = Keyboard.addListener("keyboardDidHide", () => setAndroidHeight(0));
    return () => {
      shown.remove();
      hidden.remove();
    };
  }, []);
  if (Platform.OS !== "android") return controllerHeight;
  return androidHeight > 0 ? androidHeight + bottom : 0;
}

/**
 * A sheet's heading, as one shared component.
 *
 * `Sheet` renders this itself when given a `title`, which is preferred, because the caller then
 * writes no markup. This export is for headings that cannot use the prop: one that scrolls with the
 * body rather than pinning above it, or one sharing its row with a control no prop describes (a
 * back chevron, a badge, a "Use best match" button). Those keep their structure and take their
 * typography from here.
 *
 * It is a component rather than a class string because of `accessibilityRole="header"`. A missing
 * role is invisible in review: the heading looks right, and only a screen-reader user finds they
 * cannot jump between sections.
 */
export function SheetTitle({ children, className, numberOfLines }: {
  children: ReactNode;
  className?: string;
  numberOfLines?: number;
}) {
  return (
    <Text
      accessibilityRole="header"
      className={cn("font-title text-lg text-foreground", className)}
      numberOfLines={numberOfLines}
    >
      {children}
    </Text>
  );
}
